// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronUp, Loader2, AlertCircle, Search, ExternalLink, MapPinned, Inbox, ChevronDown, Link2 } from 'lucide-react'
import {
  getRoadmap, submitVote, RoadmapItem, RoadmapResponse,
  listFeedback, triageFeedback, FeedbackItem, FeedbackInboxCounts,
} from '../api/client'
import { friendlyError } from '../api/errors'
import { useAuth } from '../auth/AuthContext'
import { useTableFunnels, type FunnelColumn } from '../components/common/TableFunnels'

interface Props {
  onScreenEnter: () => void
}

// Status taxonomy. Aligns with the migration 007 status values plus legacy.
type NormalizedStatus = 'new' | 'triaged' | 'planned' | 'in_progress' | 'done' | 'wont_fix'

function normalizeStatus(s: string): NormalizedStatus {
  switch (s) {
    case 'new':         return 'new'
    case 'triaged':     return 'triaged'
    case 'now':
    case 'next':
    case 'later':
    case 'planned':     return 'planned'
    case 'in_progress': return 'in_progress'
    case 'shipped':
    case 'done':        return 'done'
    case 'wont_fix':    return 'wont_fix'
    default:            return 'planned'
  }
}

const STATUS_LABEL: Record<NormalizedStatus, string> = {
  new: 'New', triaged: 'Triaged', planned: 'Planned',
  in_progress: 'In progress', done: 'Done', wont_fix: 'Won’t fix',
}

const STATUS_BAR_ORDER: NormalizedStatus[] = ['new', 'triaged', 'planned', 'in_progress', 'done', 'wont_fix']

// Friendly module label (lowercase-with-dashes -> Title Case).
function moduleLabel(m: string): string {
  if (!m) return 'Other'
  return m.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

// Priority bucket -> label + tone.
function priorityBadge(p: number): { label: string; bg: string; fg: string } | null {
  if (p >= 90) return { label: 'CRITICAL', bg: '#fee2e2', fg: '#991b1b' }
  if (p >= 80) return { label: 'HIGH',     bg: '#ffedd5', fg: '#9a3412' }
  if (p >= 60) return { label: 'MEDIUM',   bg: '#fef3c7', fg: '#854d0e' }
  if (p > 0)   return { label: 'LOW',      bg: '#e0e7ff', fg: '#3730a3' }
  return null
}

export default function RoadmapPage({ onScreenEnter }: Props) {
  useEffect(() => { onScreenEnter() }, [onScreenEnter])

  const [data, setData] = useState<RoadmapResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [votingIds, setVotingIds] = useState<Set<string>>(new Set())
  // D-SUPPORT-2: support tier is read-only on the roadmap (no votes, no feedback triage).
  const { role } = useAuth()
  const canVote = role !== 'support'
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('') // '' = all
  const [moduleFilter, setModuleFilter] = useState<string>('') // '' = all
  const [tab, setTab] = useState<'roadmap' | 'feedback'>('roadmap')

  useEffect(() => {
    getRoadmap()
      .then(setData)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [])

  async function handleVote(itemId: string) {
    setVotingIds(s => new Set(s).add(itemId))
    try {
      const result = await submitVote(itemId)
      setData(prev => {
        if (!prev) return prev
        return {
          ...prev,
          items: prev.items.map(i => i.id === itemId ? { ...i, voteCount: result.vote_count } : i),
          now:     prev.now.map(i => i.id === itemId ? { ...i, voteCount: result.vote_count } : i),
          next:    prev.next.map(i => i.id === itemId ? { ...i, voteCount: result.vote_count } : i),
          later:   prev.later.map(i => i.id === itemId ? { ...i, voteCount: result.vote_count } : i),
          shipped: prev.shipped.map(i => i.id === itemId ? { ...i, voteCount: result.vote_count } : i),
        }
      })
    } catch {
      // Silent — voting is best-effort
    } finally {
      setVotingIds(s => { const n = new Set(s); n.delete(itemId); return n })
    }
  }

  // Apply search + filters to the items list
  const filtered = useMemo(() => {
    if (!data) return []
    const s = search.trim().toLowerCase()
    return data.items.filter(i => {
      if (statusFilter && normalizeStatus(i.status) !== statusFilter) return false
      if (moduleFilter && i.category !== moduleFilter) return false
      if (s && !i.title.toLowerCase().includes(s) && !(i.description ?? '').toLowerCase().includes(s)) return false
      return true
    })
  }, [data, search, statusFilter, moduleFilter])

  // Bucket the (filtered) items into the 3 kanban columns we display.
  const columns = useMemo(() => {
    const planned: RoadmapItem[] = []
    const inProgress: RoadmapItem[] = []
    const done: RoadmapItem[] = []
    for (const i of filtered) {
      const s = normalizeStatus(i.status)
      if (s === 'in_progress') inProgress.push(i)
      else if (s === 'done') done.push(i)
      else if (s === 'planned' || s === 'new' || s === 'triaged') planned.push(i)
    }
    return { planned, inProgress, done }
  }, [filtered])

  if (loading) {
    return (
      <div style={loadingWrap}>
        <Loader2 size={28} style={{ color: 'var(--color-primary)' }} className="spin" />
        <span style={{ marginLeft: 10, color: 'var(--color-text-soft)' }}>Loading roadmap…</span>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div style={errorWrap}>
        <AlertCircle size={20} style={{ marginRight: 8 }} />
        {error ?? 'Roadmap not available'}
      </div>
    )
  }

  return (
    <div style={pageStyle}>
      {/* Page header */}
      <header style={pageHeader}>
        <div>
          <h1 style={titleStyle}>Roadmap &amp; Feedback</h1>
          <p style={subtitleStyle}>
            Track project progress · Submit feedback · Discuss with the team
          </p>
        </div>
      </header>

      {/* Tabs */}
      <div style={tabBar}>
        <button
          onClick={() => setTab('roadmap')}
          style={{ ...tabBtn, ...(tab === 'roadmap' ? tabBtnActive : {}) }}
        >
          <MapPinned size={14} />
          <span>Roadmap</span>
        </button>
        <button
          onClick={() => setTab('feedback')}
          style={{ ...tabBtn, ...(tab === 'feedback' ? tabBtnActive : {}) }}
        >
          <Inbox size={14} />
          <span>Feedback inbox</span>
        </button>
      </div>

      {tab === 'roadmap' && <>

      {/* Search + filters row */}
      <section style={filterRow}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--color-text-mute)' }} />
          <input
            type="text"
            placeholder="Search title or description…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...filterInput, paddingLeft: 32, width: '100%' }}
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={filterInput}>
          <option value="">All statuses</option>
          {STATUS_BAR_ORDER.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} style={filterInput}>
          <option value="">All modules</option>
          {data.by_module.map(m => <option key={m.module} value={m.module}>{moduleLabel(m.module)}</option>)}
        </select>
      </section>

      {/* Overall progress card */}
      <section style={overallCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <h2 style={overallTitle}>Overall progress</h2>
          <span style={overallPct}>{data.overall.pct}%</span>
        </div>
        <ProgressBar pct={data.overall.pct} tone="success" thickness={10} />
        <p style={overallSub}>
          {data.overall.done} of {data.overall.total} items done
        </p>

        {/* By module — two-column grid like Pharmachain */}
        <div style={byModuleHeader}>BY MODULE</div>
        <div style={byModuleGrid}>
          {data.by_module.map(m => (
            <ModuleRow
              key={m.module}
              label={moduleLabel(m.module)}
              done={m.done}
              total={m.total}
              pct={m.pct}
              hasInProgress={m.in_progress > 0}
              onClick={() => setModuleFilter(moduleFilter === m.module ? '' : m.module)}
              active={moduleFilter === m.module}
            />
          ))}
        </div>
      </section>

      {/* Status counter strip */}
      <section style={statusStrip}>
        {STATUS_BAR_ORDER.map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
            style={{
              ...statusCell,
              background: statusFilter === s ? 'var(--color-surface-alt)' : 'var(--color-surface)',
              borderColor: statusFilter === s ? 'var(--color-primary)' : 'var(--color-border)',
            }}
          >
            <div style={statusLabel}>{STATUS_LABEL[s].toUpperCase()}</div>
            <div style={statusValue}>{(data.status_counts as any)[s]}</div>
          </button>
        ))}
      </section>

      {/* Kanban columns */}
      <section style={kanbanGrid}>
        <KanbanColumn
          title="Planned" tone="#f59e0b"
          items={columns.planned}
          onVote={handleVote} votingIds={votingIds} canVote={canVote}
        />
        <KanbanColumn
          title="In progress" tone="#0891b2"
          items={columns.inProgress}
          onVote={handleVote} votingIds={votingIds} canVote={canVote}
        />
        <KanbanColumn
          title="Done" tone="#10b981"
          items={columns.done}
          onVote={handleVote} votingIds={votingIds} canVote={canVote}
          collapsed
        />
      </section>

      </>}

      {tab === 'feedback' && (
        <FeedbackInbox roadmapItems={data.items} />
      )}
    </div>
  )
}

// -------------------- Sub-components --------------------

function ModuleRow({
  label, done, total, pct, hasInProgress, onClick, active,
}: { label: string; done: number; total: number; pct: number; hasInProgress: boolean; onClick: () => void; active: boolean }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...moduleRowStyle,
        background: active ? 'var(--color-surface-alt)' : 'transparent',
      }}
      title={`Filter by ${label}`}
    >
      <span style={moduleRowLabel}>{label}</span>
      <ProgressBar
        pct={pct}
        tone={pct === 100 ? 'success' : (hasInProgress ? 'info' : 'mute')}
        thickness={6}
      />
      <span style={moduleRowPct}>{pct}%</span>
      <span style={moduleRowRatio}>{done}/{total}</span>
    </button>
  )
}

function ProgressBar({ pct, tone, thickness = 8 }: { pct: number; tone: 'success' | 'info' | 'mute'; thickness?: number }) {
  const color =
    tone === 'success' ? '#10b981'
    : tone === 'info'  ? '#3b82f6'
    : '#9ca3af'
  return (
    <div style={{
      flex: 1,
      height: thickness,
      background: '#e5e7eb',
      borderRadius: thickness / 2,
      overflow: 'hidden',
      minWidth: 80,
    }}>
      <div style={{
        width: `${Math.max(0, Math.min(100, pct))}%`,
        height: '100%',
        background: color,
        transition: 'width 400ms ease',
      }} />
    </div>
  )
}

function KanbanColumn({
  title, tone, items, onVote, votingIds, collapsed = false, canVote = true,
}: {
  title: string; tone: string; items: RoadmapItem[];
  onVote: (id: string) => void; votingIds: Set<string>; collapsed?: boolean; canVote?: boolean;
}) {
  const [expandedAll, setExpandedAll] = useState(!collapsed)
  const visible = expandedAll ? items : items.slice(0, 8)
  return (
    <div>
      <header style={{ ...kanbanHeader, borderTopColor: tone }}>
        <span style={kanbanTitle}>{title}</span>
        <span style={kanbanCount}>{items.length}</span>
      </header>
      <div style={kanbanBody}>
        {items.length === 0 && (
          <div style={kanbanEmpty}>Nothing in this column.</div>
        )}
        {visible.map(item => (
          <RoadmapCard
            key={item.id}
            item={item}
            onVote={onVote}
            canVote={canVote}
            voting={votingIds.has(item.id)}
          />
        ))}
        {!expandedAll && items.length > 8 && (
          <button onClick={() => setExpandedAll(true)} style={showMoreBtn}>
            Show {items.length - 8} more…
          </button>
        )}
      </div>
    </div>
  )
}

function RoadmapCard({ item, onVote, voting, canVote = true }: { item: RoadmapItem; onVote: (id: string) => void; voting: boolean; canVote?: boolean }) {
  const pri = priorityBadge(item.priority)
  const navigate = useNavigate()
  return (
    <article
      style={{ ...cardStyle, cursor: 'pointer' }}
      onClick={() => navigate(`/roadmap/${item.id}`)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/roadmap/${item.id}`) } }}
      role="link"
      tabIndex={0}
      aria-label={`Open details for ${item.title}`}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
        <h3 style={cardTitle}>{item.title}</h3>
        {pri && <span style={{ ...badgeStyle, background: pri.bg, color: pri.fg }}>{pri.label}</span>}
      </div>
      {item.description && item.description !== item.title && (
        <p style={cardDesc}>{item.description}</p>
      )}
      <div style={cardMeta}>
        <span style={cardMetaPill}>{moduleLabel(item.category)}</span>
        {item.phase && <span style={cardMetaPillMute}>{item.phase}</span>}
        {item.shippedInPr && (
          <span style={prPill} title={item.shippedInPr}>
            <ExternalLink size={11} style={{ verticalAlign: 'text-bottom', marginRight: 3 }} />
            {item.shippedInPr}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {/* Voting (does not navigate). D-SUPPORT-2: the support tier reads the roadmap but
            does not vote (Patrick 2026-08-17 — roadmap is read-only for support); the count
            still renders as a plain pill so the information isn't lost. */}
        {canVote ? (
          <button
            onClick={(e) => { e.stopPropagation(); onVote(item.id) }}
            disabled={voting}
            aria-label={`Upvote ${item.title}`}
            style={voteBtn}
          >
            <ChevronUp size={14} />
            <span>{item.voteCount}</span>
          </button>
        ) : (
          <span style={cardMetaPillMute} aria-label={`${item.voteCount} votes`}>
            <ChevronUp size={11} style={{ verticalAlign: 'text-bottom' }} /> {item.voteCount}
          </span>
        )}
      </div>
    </article>
  )
}

// -------------------- Feedback Inbox --------------------

const FB_CATEGORY_LABEL: Record<string, string> = {
  bug: 'Bug',
  feature_request: 'Feature request',
  ux: 'UX',
  data_correction: 'Data correction',
  patient_care: 'Patient care',
  other: 'Other',
}

function severityTone(s: string | null) {
  if (s === 'high')   return { bg: '#fee2e2', fg: '#991b1b' }
  if (s === 'medium') return { bg: '#fef3c7', fg: '#854d0e' }
  if (s === 'low')    return { bg: '#dcfce7', fg: '#166534' }
  return { bg: '#f3f4f6', fg: '#4b5563' }
}

function statusTone(s: string) {
  if (s === 'new')     return { bg: '#dbeafe', fg: '#1e40af' }
  if (s === 'triaged') return { bg: '#fef3c7', fg: '#854d0e' }
  if (s === 'linked')  return { bg: '#e0e7ff', fg: '#3730a3' }
  if (s === 'closed')  return { bg: '#f3f4f6', fg: '#4b5563' }
  return { bg: '#f3f4f6', fg: '#4b5563' }
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime()
  const ms = Date.now() - t
  const m  = Math.floor(ms / 60000)
  if (m < 1)    return 'just now'
  if (m < 60)   return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24)   return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 7)    return `${d}d ago`
  return new Date(iso).toLocaleDateString()
}

// D-UI-2: what each column of the feedback inbox shows, for its header funnel.
const FEEDBACK_FUNNELS: FunnelColumn<FeedbackItem>[] = [
  { key: 'when',     label: 'When',     value: f => f.submitted_at },
  { key: 'role',     label: 'Role',     value: f => f.submitter_role ?? 'anon' },
  { key: 'screen',   label: 'Screen',   value: f => f.screen },
  { key: 'category', label: 'Category', value: f => FB_CATEGORY_LABEL[f.category] ?? f.category },
  { key: 'severity', label: 'Severity', value: f => f.severity ? f.severity.toUpperCase() : null },
  { key: 'message',  label: 'Message',  value: f => f.message, kind: 'text' },
  { key: 'status',   label: 'Status',   value: f => f.status.toUpperCase() },
]

function FeedbackInbox({ roadmapItems }: { roadmapItems: RoadmapItem[] }) {
  const [items, setItems] = useState<FeedbackItem[]>([])
  const [counts, setCounts] = useState<FeedbackInboxCounts>({ total: 0, status_new: 0, triaged: 0, linked: 0, closed: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorStatus, setErrorStatus] = useState<number | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('') // '' = all
  const [expandedId, setExpandedId] = useState<string | null>(null)

  function reload() {
    setLoading(true); setError(null); setErrorStatus(null)
    listFeedback({ status: statusFilter || undefined, limit: 200 })
      .then(r => { setItems(r.items); setCounts(r.counts) })
      .catch(e => {
        setError(friendlyError(e))
        setErrorStatus(typeof e?.status === 'number' ? e.status : null)
      })
      .finally(() => setLoading(false))
  }
  useEffect(reload, [statusFilter])

  // D-UI-2: the header funnels filter the whole loaded inbox.
  const fx = useTableFunnels(items, FEEDBACK_FUNNELS)

  if (loading) {
    return (
      <div style={loadingWrap}>
        <Loader2 size={20} className="spin" style={{ color: 'var(--color-primary)' }} />
        <span style={{ marginLeft: 10, color: 'var(--color-text-soft)' }}>Loading feedback…</span>
      </div>
    )
  }

  // Friendly empty state for not-signed-in / wrong role
  if (errorStatus === 401 || errorStatus === 403) {
    const isAnon = errorStatus === 401
    return (
      <div style={fbAuthEmpty}>
        <Inbox size={36} style={{ color: 'var(--color-text-mute)', marginBottom: 12 }} />
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
          {isAnon ? 'Sign in to view feedback' : 'Admin access required'}
        </h3>
        <p style={{ margin: '8px 0 16px', color: 'var(--color-text-soft)', fontSize: 13, maxWidth: 420 }}>
          {isAnon
            ? 'The feedback inbox is only available to OwlRx admins. Sign in with the Admin role to triage submitted feedback.'
            : 'Your current role doesn\u2019t have permission to view the feedback inbox. Switch to the Admin role from the sign-in screen.'}
        </p>
        <a href="/login" style={fbAuthCta}>
          {isAnon ? 'Sign in' : 'Switch role'}
        </a>
      </div>
    )
  }

  if (error) {
    return (
      <div style={errorWrap}>
        <AlertCircle size={20} style={{ marginRight: 8 }} />
        {error}
      </div>
    )
  }

  const chips: Array<{ key: string; label: string; n: number }> = [
    { key: '',        label: 'All',     n: counts.total },
    { key: 'new',     label: 'New',     n: counts.status_new },
    { key: 'triaged', label: 'Triaged', n: counts.triaged },
    { key: 'linked',  label: 'Linked',  n: counts.linked },
    { key: 'closed',  label: 'Closed',  n: counts.closed },
  ]

  return (
    <>
      {/* Filter chips */}
      <div style={fbChipRow}>
        {chips.map(c => (
          <button
            key={c.key || 'all'}
            onClick={() => setStatusFilter(c.key)}
            style={{
              ...fbChip,
              ...(statusFilter === c.key ? fbChipActive : {}),
            }}
          >
            {c.label}
            <span style={fbChipCount}>{c.n}</span>
          </button>
        ))}
      </div>

      {/* Inbox table */}
      {fx.chips}
      <div style={fbTableWrap}>
        {items.length === 0 ? (
          <div style={{ ...kanbanEmpty, padding: 40 }}>
            No feedback {statusFilter ? `with status "${statusFilter}"` : 'yet'}.
          </div>
        ) : (
          <>
            <div style={fbTableHeader}>
              <div style={{ width: 90 }}>When {fx.funnel('when')}</div>
              <div style={{ width: 90 }}>Role {fx.funnel('role')}</div>
              <div style={{ width: 130 }}>Screen {fx.funnel('screen')}</div>
              <div style={{ width: 110 }}>Category {fx.funnel('category')}</div>
              <div style={{ width: 80 }}>Severity {fx.funnel('severity')}</div>
              <div style={{ flex: 1, minWidth: 200 }}>Message {fx.funnel('message')}</div>
              <div style={{ width: 80 }}>Status {fx.funnel('status')}</div>
              <div style={{ width: 22 }} />
            </div>
            {fx.rows.map(item => (
              <FeedbackRow
                key={item.id}
                item={item}
                expanded={expandedId === item.id}
                onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                onTriaged={reload}
                roadmapItems={roadmapItems}
              />
            ))}
          </>
        )}
      </div>
    </>
  )
}

function FeedbackRow({
  item, expanded, onToggle, onTriaged, roadmapItems,
}: {
  item: FeedbackItem
  expanded: boolean
  onToggle: () => void
  onTriaged: () => void
  roadmapItems: RoadmapItem[]
}) {
  const sev = severityTone(item.severity)
  const stat = statusTone(item.status)
  return (
    <div>
      <button onClick={onToggle} style={fbRow}>
        <div style={{ width: 90, fontSize: 12, color: 'var(--color-text-mute)' }}>
          {relTime(item.submitted_at)}
        </div>
        <div style={{ width: 90, fontSize: 12 }}>
          {item.submitter_role ?? <span style={{ color: 'var(--color-text-mute)' }}>anon</span>}
        </div>
        <div style={{ width: 130, fontSize: 12, fontFamily: 'ui-monospace, monospace', color: 'var(--color-text-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.screen}
        </div>
        <div style={{ width: 110 }}>
          <span style={{ ...cardMetaPill, fontSize: 11 }}>
            {FB_CATEGORY_LABEL[item.category] ?? item.category}
          </span>
        </div>
        <div style={{ width: 80 }}>
          {item.severity && (
            <span style={{ ...badgeStyle, background: sev.bg, color: sev.fg }}>
              {item.severity.toUpperCase()}
            </span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 200, fontSize: 13, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.message}
        </div>
        <div style={{ width: 80 }}>
          <span style={{ ...badgeStyle, background: stat.bg, color: stat.fg }}>
            {item.status.toUpperCase()}
          </span>
        </div>
        <div style={{ width: 22, color: 'var(--color-text-mute)' }}>
          <ChevronDown size={14} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 120ms ease' }} />
        </div>
      </button>
      {expanded && (
        <FeedbackTriagePanel item={item} roadmapItems={roadmapItems} onTriaged={onTriaged} />
      )}
    </div>
  )
}

function FeedbackTriagePanel({
  item, roadmapItems, onTriaged,
}: { item: FeedbackItem; roadmapItems: RoadmapItem[]; onTriaged: () => void }) {
  const [status, setStatus] = useState(item.status)
  const [linkedId, setLinkedId] = useState<string>(item.linked_roadmap_id ?? '')
  const [notes, setNotes] = useState<string>(item.triage_notes ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save() {
    setBusy(true); setErr(null)
    try {
      await triageFeedback(item.id, {
        status,
        linked_roadmap_id: linkedId || null,
        triage_notes: notes || undefined,
      })
      onTriaged()
    } catch (e: any) {
      setErr(friendlyError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={fbExpanded}>
      <div style={{ marginBottom: 12 }}>
        <div style={fbExpandedLabel}>Full message</div>
        <div style={fbMessage}>{item.message}</div>
      </div>

      <div style={fbMetaRow}>
        {item.submitter_email && <span className="email-text"><strong>From:</strong> {item.submitter_email}</span>}
        {item.viewport && <span><strong>Viewport:</strong> {item.viewport}</span>}
        {item.triaged_at && <span><strong>Last triaged:</strong> {relTime(item.triaged_at)} by {item.triaged_by ?? 'unknown'}</span>}
      </div>

      <div style={fbTriageGrid}>
        <div>
          <label style={fbExpandedLabel}>Status</label>
          <select value={status} onChange={e => setStatus(e.target.value as any)} style={filterInput}>
            <option value="new">New</option>
            <option value="triaged">Triaged</option>
            <option value="linked">Linked to roadmap</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div>
          <label style={fbExpandedLabel}>Link to roadmap item</label>
          <select value={linkedId} onChange={e => setLinkedId(e.target.value)} style={filterInput}>
            <option value="">— none —</option>
            {roadmapItems.map(r => (
              <option key={r.id} value={r.id}>{r.title}</option>
            ))}
          </select>
          {item.linked_roadmap_title && (
            <div style={{ fontSize: 11, color: 'var(--color-text-mute)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Link2 size={11} /> Currently linked: {item.linked_roadmap_title}
            </div>
          )}
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <label style={fbExpandedLabel}>Triage notes</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            style={{ ...filterInput, width: '100%', fontFamily: 'inherit', resize: 'vertical' }}
            placeholder="Internal notes, decisions, follow-ups…"
          />
        </div>
      </div>

      {err && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 8 }}>{err}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button onClick={save} disabled={busy} style={fbSaveBtn}>
          {busy ? 'Saving…' : 'Save triage'}
        </button>
      </div>
    </div>
  )
}

// -------------------- Styles --------------------

const pageStyle: React.CSSProperties = {
  maxWidth: 'var(--page-max, 1400px)', margin: '0 auto', padding: '24px',
}
const pageHeader: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18,
}
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 30, fontWeight: 700, margin: 0, letterSpacing: -0.5,
}
const subtitleStyle: React.CSSProperties = {
  margin: '4px 0 0', color: 'var(--color-text-soft)', fontSize: 13,
}

const filterRow: React.CSSProperties = {
  display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center',
}
const filterInput: React.CSSProperties = {
  padding: '10px 12px', border: '1px solid var(--color-border)',
  borderRadius: 6, fontSize: 13, background: 'var(--color-surface)', fontFamily: 'inherit',
}

const overallCard: React.CSSProperties = {
  background: '#f0fdf4',
  border: '1px solid #bbf7d0',
  borderRadius: 8,
  padding: '16px 20px',
  marginBottom: 16,
}
const overallTitle: React.CSSProperties = {
  margin: 0, fontSize: 18, fontWeight: 600, color: '#14532d',
}
const overallPct: React.CSSProperties = {
  fontFamily: 'Georgia, serif',
  fontSize: 26, fontWeight: 700, color: '#10b981',
}
const overallSub: React.CSSProperties = {
  margin: '8px 0 14px', fontSize: 12, color: '#166534',
}
const byModuleHeader: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, letterSpacing: 1,
  color: '#166534', marginTop: 16, marginBottom: 8,
}
const byModuleGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
  rowGap: 4, columnGap: 28,
}
const moduleRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '120px 1fr 36px 36px',
  gap: 10, alignItems: 'center',
  padding: '4px 8px',
  border: 'none',
  cursor: 'pointer', borderRadius: 4,
  fontFamily: 'inherit', fontSize: 12,
  textAlign: 'left',
}
const moduleRowLabel: React.CSSProperties = {
  fontSize: 12, color: '#1f2937', fontWeight: 500,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}
const moduleRowPct: React.CSSProperties = {
  fontSize: 11, color: '#6b7280', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
}
const moduleRowRatio: React.CSSProperties = {
  fontSize: 11, color: '#9ca3af', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
}

const statusStrip: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  gap: 10, marginBottom: 16,
}
const statusCell: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: '14px 16px',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'all 120ms ease',
  fontFamily: 'inherit',
}
const statusLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, letterSpacing: 1,
  color: 'var(--color-text-mute)',
}
const statusValue: React.CSSProperties = {
  fontFamily: 'Georgia, serif',
  fontSize: 26, fontWeight: 700, marginTop: 4,
  fontVariantNumeric: 'tabular-nums',
}

const kanbanGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 16,
}
const kanbanHeader: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '10px 14px',
  background: 'var(--color-surface)',
  borderTop: '3px solid',
  borderLeft: '1px solid var(--color-border)',
  borderRight: '1px solid var(--color-border)',
  borderRadius: '6px 6px 0 0',
}
const kanbanTitle: React.CSSProperties = {
  fontSize: 14, fontWeight: 600,
}
const kanbanCount: React.CSSProperties = {
  fontSize: 12, color: 'var(--color-text-mute)',
  background: 'var(--color-surface-alt)',
  padding: '2px 8px', borderRadius: 10,
  fontVariantNumeric: 'tabular-nums',
}
const kanbanBody: React.CSSProperties = {
  background: 'var(--color-surface-alt)',
  border: '1px solid var(--color-border)',
  borderTop: 'none',
  borderRadius: '0 0 6px 6px',
  padding: 10,
  display: 'flex', flexDirection: 'column', gap: 8,
  minHeight: 200,
}
const kanbanEmpty: React.CSSProperties = {
  fontSize: 12, color: 'var(--color-text-mute)',
  textAlign: 'center', padding: 20,
}
const showMoreBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px dashed var(--color-border)',
  color: 'var(--color-text-soft)',
  fontSize: 12, padding: '8px',
  borderRadius: 4,
  fontFamily: 'inherit',
  cursor: 'pointer',
}

const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  padding: 12,
}
const cardTitle: React.CSSProperties = {
  margin: 0, fontSize: 14, fontWeight: 600, lineHeight: 1.35,
}
const cardDesc: React.CSSProperties = {
  margin: '6px 0 0',
  fontSize: 12, color: 'var(--color-text-soft)', lineHeight: 1.45,
}
const cardMeta: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, alignItems: 'center',
}
const cardMetaPill: React.CSSProperties = {
  fontSize: 11, fontWeight: 500,
  background: '#f3f4f6', color: '#374151',
  padding: '2px 8px', borderRadius: 10,
}
const cardMetaPillMute: React.CSSProperties = {
  fontSize: 11,
  background: 'transparent', color: 'var(--color-text-mute)',
  padding: '2px 6px',
}
const prPill: React.CSSProperties = {
  fontSize: 11, fontWeight: 500,
  background: '#dbeafe', color: '#1e40af',
  padding: '2px 8px', borderRadius: 10,
}
const badgeStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
  padding: '2px 8px', borderRadius: 10,
  whiteSpace: 'nowrap',
}
const voteBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 2,
  background: 'transparent', border: '1px solid var(--color-border)',
  borderRadius: 12, padding: '2px 8px',
  fontSize: 12, fontVariantNumeric: 'tabular-nums',
  color: 'var(--color-text-soft)',
  cursor: 'pointer', fontFamily: 'inherit',
}

const loadingWrap: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', alignItems: 'center',
  padding: '80px 20px', minHeight: 300,
}
const errorWrap: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 40, color: 'var(--color-danger, #b91c1c)',
}

// Tabs
const tabBar: React.CSSProperties = {
  display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border)',
  marginBottom: 16,
}
const tabBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '10px 16px',
  background: 'transparent', border: 'none',
  borderBottom: '2px solid transparent', marginBottom: -1,
  fontSize: 14, fontWeight: 500, color: 'var(--color-text-soft)',
  cursor: 'pointer', fontFamily: 'inherit',
}
const tabBtnActive: React.CSSProperties = {
  color: 'var(--color-text, #111827)',
  borderBottomColor: 'var(--color-text, #111827)',
}

// Feedback inbox
const fbChipRow: React.CSSProperties = {
  display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap',
}
const fbChip: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  padding: '6px 12px',
  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
  borderRadius: 14, fontSize: 13, color: 'var(--color-text-soft)',
  cursor: 'pointer', fontFamily: 'inherit',
}
const fbChipActive: React.CSSProperties = {
  background: '#1f2937', color: '#fff', borderColor: '#1f2937',
}
const fbChipCount: React.CSSProperties = {
  fontSize: 11, fontWeight: 600,
  background: 'rgba(255,255,255,0.15)',
  padding: '1px 6px', borderRadius: 8,
  fontVariantNumeric: 'tabular-nums',
}
const fbTableWrap: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 6,
  background: 'var(--color-surface)', overflow: 'hidden',
}
const fbTableHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '10px 14px',
  background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
  color: 'var(--color-text-mute)', textTransform: 'uppercase',
}
const fbRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 14px', width: '100%',
  background: 'transparent', border: 'none', borderBottom: '1px solid var(--color-border)',
  fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer',
}
const fbExpanded: React.CSSProperties = {
  padding: '16px 18px', background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)',
}
const fbExpandedLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
  color: 'var(--color-text-mute)', textTransform: 'uppercase',
  display: 'block', marginBottom: 4,
}
const fbMessage: React.CSSProperties = {
  fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)', borderRadius: 4,
  padding: 12,
}
const fbMetaRow: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 16,
  fontSize: 11, color: 'var(--color-text-mute)',
  marginBottom: 12,
}
const fbTriageGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
}
const fbSaveBtn: React.CSSProperties = {
  background: '#1f2937', color: '#fff', border: 'none',
  padding: '8px 16px', borderRadius: 4, fontSize: 13, fontWeight: 500,
  cursor: 'pointer', fontFamily: 'inherit',
}
const fbAuthEmpty: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  textAlign: 'center',
  padding: '60px 20px',
  background: 'var(--color-surface)',
  border: '1px dashed var(--color-border)',
  borderRadius: 8,
}
const fbAuthCta: React.CSSProperties = {
  display: 'inline-block',
  background: 'var(--color-primary, #047857)', color: '#fff',
  padding: '8px 20px', borderRadius: 4,
  fontSize: 13, fontWeight: 500, textDecoration: 'none',
}
